import {
  createMotionViewportCanvas,
  ensureMotionExportColorAssets,
  getEffectiveMotionPlaybackDuration,
  getMotionAtlasFrameTime,
  getMotionFrameIndex,
  getMotionResizeDimensions,
  getMotionSequenceDuration,
  getMotionSequencePlaybackTime,
  interpolateTrackingQuadAtTime,
  renderFrame,
  seekMotionColorMatteStrict,
  seekMotionVideos,
  type LoadedMotionAssets,
  type MotionRenderOptions
} from './motion';
import { isBackgroundTintActive, resolveActiveMatteChannels } from './colorMatte';
import { VideoDecoderPipeline } from './VideoDecoderPipeline';
import { UserVideoDecoderPipeline } from './UserVideoDecoderPipeline';
import { EncoderBackpressureGate } from './EncoderBackpressureGate';
import {
  getRenderAbortError,
  throwIfRenderAborted,
  waitForRenderOperation,
  yieldToRenderControls
} from './RenderTask';
import { StreamingMp4Muxer } from './streamingMp4Muxer';
import {
  prepareFirstUserAudioTrack,
  type PreparedUserAudioTrack
} from './userAudioTrack';
import type { VideoExportArtifact } from './videoExportStorage';
import type { MotionResizeMode } from '../types';

const MOTION_EXPORT_SIZE = 1080;
const DEFAULT_MOTION_GOP = 30;
const MOTION_EXPORT_SCREEN_RENDER_SCALE = 2;
const MOTION_EXPORT_BITRATE = 20_000_000;

export interface MotionVideoExportOptions extends MotionRenderOptions {
  resizeMode?: MotionResizeMode;
  onProgress?: (completedFrames: number, totalFrames: number) => void;
  onStageChange?: (stage: 'rendering' | 'packaging') => void;
  /** URL of the user-supplied video (blob: or https:). When provided the export
   *  uses a fast WebCodecs sequential decoder instead of HTMLVideoElement.seek. */
  userSourceUrl?: string;
  userSourceUrl2?: string;
  userSourceUrl3?: string;
  outputFileHandle?: FileSystemFileHandle | null;
  signal?: AbortSignal;
}

export function isMotionVideoExportSupported(): boolean {
  return typeof VideoEncoder !== 'undefined' && typeof VideoFrame !== 'undefined';
}

/**
 * Tries H.264 codec profiles from most compatible to most capable until one
 * passes VideoEncoder.isConfigSupported(). Handles browsers that throw instead
 * of returning {supported:false} (e.g. older Safari).
 */
async function findSupportedEncoderConfig(
  baseConfig: VideoEncoderConfig
): Promise<VideoEncoderConfig> {
  if (typeof VideoEncoder.isConfigSupported !== 'function') {
    return baseConfig;
  }

  // avc1.4d0028 = Main Profile Level 4.0  — handles 1080×1080@30 fps, supported on mobile/Safari
  // avc1.42C028 = Baseline Profile Level 4.0 — most universally compatible fallback
  // avc1.4d0033 = Main Profile Level 5.1    — original (desktop Chrome/Firefox)
  const codecCandidates = ['avc1.4d0028', 'avc1.42C028', 'avc1.4d0033'];
  // Safari may reject 'prefer-hardware' when HW is unavailable for a given codec+resolution.
  // Try hardware first (faster), then fall back to software.
  const hwCandidates: Array<VideoEncoderConfig['hardwareAcceleration']> = [
    'prefer-hardware',
    'no-preference'
  ];

  for (const hardwareAcceleration of hwCandidates) {
    for (const codec of codecCandidates) {
      const config: VideoEncoderConfig = { ...baseConfig, codec, hardwareAcceleration };
      try {
        const support = await VideoEncoder.isConfigSupported(config);
        if (support.supported) {
          return config;
        }
      } catch {
        // isConfigSupported throws in some browsers (e.g. older Safari) — try next.
      }
    }
  }

  throw new Error(
    'WebCodecs H.264 encoder не поддерживается в данном браузере. ' +
    'Попробуйте Chrome или обновите браузер.'
  );
}

async function initializeAuxiliaryDecoder(
  url: string,
  signal: AbortSignal | undefined,
  label: string
): Promise<VideoDecoderPipeline> {
  const candidate = new VideoDecoderPipeline(url, signal);
  try {
    await candidate.initialize();
    return candidate;
  } catch (error) {
    candidate.close();
    throw new Error(
      `${label} decoder failed: ${error instanceof Error ? error.message : 'unknown error'}`
    );
  }
}

export async function renderMotionExport(
  motionAssets: LoadedMotionAssets,
  userSource: CanvasImageSource | null,
  options: MotionVideoExportOptions = {}
): Promise<VideoExportArtifact> {
  throwIfRenderAborted(options.signal);
  if (!isMotionVideoExportSupported()) {
    throw new Error(
      'Для экспорта анимации откройте сервис во внешнем браузере или обновите систему на свежую версию.'
    );
  }
  await ensureMotionExportColorAssets(motionAssets, options.colorMatte);

  const fps = 30; // Жестко ограничиваем до 30 кадров для стабильной выдачи
  const motionLoop = motionAssets.template.motionLoop === true;
  const templateDuration = getMotionSequenceDuration(motionAssets.tracking);
  const duration = Math.max(
    getEffectiveMotionPlaybackDuration(motionAssets.tracking, userSource, motionLoop),
    options.userSource2
      ? getEffectiveMotionPlaybackDuration(motionAssets.tracking, options.userSource2, motionLoop)
      : 0,
    options.userSource3
      ? getEffectiveMotionPlaybackDuration(motionAssets.tracking, options.userSource3, motionLoop)
      : 0
  );
  const totalFrames = Math.max(1, Math.ceil(duration * fps));
  const resizeMode = options.resizeMode ?? '1:1';
  const internalSquareRenderSize = Math.max(
    1,
    Math.round(options.renderSize ?? (resizeMode !== '1:1' ? motionAssets.template.outputSize || 1920 : MOTION_EXPORT_SIZE))
  );
  const exportViewport = getMotionResizeDimensions(resizeMode, internalSquareRenderSize);
  const encoderConfig = await waitForRenderOperation(
    findSupportedEncoderConfig({
      codec: 'avc1.4d0028', // start with widely-compatible Main Profile Level 4.0
      width: exportViewport.outputWidth,
      height: exportViewport.outputHeight,
      bitrate: MOTION_EXPORT_BITRATE,
      framerate: fps,
      hardwareAcceleration: 'prefer-hardware',
      avc: {
        format: 'avc'
      }
    }),
    {
      signal: options.signal,
      label: 'Video encoder capability check'
    }
  );

  let encoderGate: EncoderBackpressureGate | null = null;
  let muxer: StreamingMp4Muxer | null = null;
  const encoder = new VideoEncoder({
    output: (chunk, meta) => {
      try {
        if (!muxer) {
          throw new Error('MP4 streaming output is not initialized.');
        }
        muxer.enqueueVideoChunk(chunk, meta);
        encoderGate?.notifyOutput();
      } catch (error) {
        encoderGate?.fail(error);
      }
    },
    error: (error) => {
      encoderGate?.fail(error);
    }
  });
  encoderGate = new EncoderBackpressureGate(() => encoder.encodeQueueSize);

  const matteChannelsActive = resolveActiveMatteChannels(
    motionAssets.template.motionColorMatte?.channels,
    options.colorMatte
  ).length > 0;
  const backgroundRequested = isBackgroundTintActive(
    Boolean(motionAssets.backgroundMatteUrl),
    options.colorMatte
  );
  if (matteChannelsActive && !motionAssets.colorMatteVideo) {
    throw new Error('Custom color assets are not ready. Retry the export.');
  }
  if (backgroundRequested && !motionAssets.backgroundMatteVideo) {
    throw new Error('Background color assets are not ready. Retry the export.');
  }
  // Export decodes the same atlas the preview drew from, so a recoloured export
  // matches what the viewer approved on screen.
  const atlasUrl =
    (matteChannelsActive && motionAssets.neutralAtlasUrl) ||
    motionAssets.atlasVideo.currentSrc ||
    motionAssets.atlasVideo.src;
  let pipeline = new VideoDecoderPipeline(atlasUrl, options.signal);
  let activeAtlasCycle = 0;
  let userPipeline: UserVideoDecoderPipeline | null = null;
  let userPipeline2: UserVideoDecoderPipeline | null = null;
  let userPipeline3: UserVideoDecoderPipeline | null = null;
  let userAudio: PreparedUserAudioTrack | null = null;
  let audioWriteResult: Promise<Error | null> | null = null;
  // The Puzzle Matte gets its own decoder driven by the same atlas timestamps.
  // It is only created when the recoloured result would differ from the plain
  // one, so an untouched export never pays for a second decode.
  const matteUrl = motionAssets.colorMatteVideo
    ? motionAssets.colorMatteVideo.currentSrc || motionAssets.colorMatteVideo.src
    : '';
  const matteActive = matteChannelsActive;
  let mattePipeline: VideoDecoderPipeline | null = null;
  // The background separator decodes on the same timestamps as the atlas, and
  // only when the viewer actually switched the Background tint on.
  const backgroundUrl = motionAssets.backgroundMatteVideo
    ? motionAssets.backgroundMatteVideo.currentSrc || motionAssets.backgroundMatteVideo.src
    : '';
  const backgroundActive = backgroundRequested;
  let backgroundPipeline: VideoDecoderPipeline | null = null;
  const handleAbort = () => {
    encoderGate?.fail(getRenderAbortError(options.signal));
  };
  options.signal?.addEventListener('abort', handleAbort, { once: true });

  try {
    userAudio = await prepareFirstUserAudioTrack(
      !motionAssets.template.flexibleDisplay && motionAssets.template.screenProfile?.id === 'triple-screen'
        ? [options.userSourceUrl]
        : [options.userSourceUrl, options.userSourceUrl2],
      duration,
      options.signal
    );
    muxer = await StreamingMp4Muxer.create({
      totalFrames,
      frameRate: fps,
      bitrate: MOTION_EXPORT_BITRATE,
      durationSeconds: duration,
      outputFileHandle: options.outputFileHandle,
      signal: options.signal,
      audio: userAudio
        ? {
            codec: userAudio.codec,
            maximumPacketCount: userAudio.maximumPacketCount,
            estimatedBytes: userAudio.estimatedBytes
          }
        : undefined,
      onError: (error) => encoderGate?.fail(error)
    });
    if (userAudio) {
      audioWriteResult = userAudio
        .writeTo((packet, metadata) => muxer!.addAudioPacket(packet, metadata), options.signal)
        .then(
          () => null,
          (error) => {
            const normalized = error instanceof Error ? error : new Error(String(error));
            encoderGate?.fail(normalized);
            return normalized;
          }
        );
    }
    encoderGate.throwIfUnavailable(options.signal);
    encoder.configure(encoderConfig);
    await pipeline.initialize();
    encoderGate.throwIfUnavailable(options.signal);

    if (backgroundActive) {
      backgroundPipeline = await initializeAuxiliaryDecoder(
        backgroundUrl,
        options.signal,
        'Background matte'
      );
      encoderGate.throwIfUnavailable(options.signal);
    }

    if (matteActive) {
      mattePipeline = await initializeAuxiliaryDecoder(
        matteUrl,
        options.signal,
        'Puzzle Matte'
      );
      encoderGate.throwIfUnavailable(options.signal);
    }

    // Fast user-video path: sequential WebCodecs decoder avoids per-frame HTMLVideoElement.seek.
    if (userSource instanceof HTMLVideoElement && options.userSourceUrl) {
      userPipeline = new UserVideoDecoderPipeline(options.userSourceUrl, options.signal);
      await userPipeline.initialize();
      encoderGate.throwIfUnavailable(options.signal);
    }
    if (options.userSource2 instanceof HTMLVideoElement && options.userSourceUrl2) {
      userPipeline2 = new UserVideoDecoderPipeline(options.userSourceUrl2, options.signal);
      await userPipeline2.initialize();
      encoderGate.throwIfUnavailable(options.signal);
    }
    if (options.userSource3 instanceof HTMLVideoElement && options.userSourceUrl3) {
      userPipeline3 = new UserVideoDecoderPipeline(options.userSourceUrl3, options.signal);
      await userPipeline3.initialize();
      encoderGate.throwIfUnavailable(options.signal);
    }

    options.onStageChange?.('rendering');
    for (let frameIndex = 0; frameIndex < totalFrames; frameIndex += 1) {
      encoderGate.throwIfUnavailable(options.signal);
      const time = frameIndex / fps;
      const atlasCycle = motionLoop && templateDuration > 0 ? Math.floor(time / templateDuration) : 0;
      if (atlasCycle !== activeAtlasCycle) {
        pipeline.close();
        pipeline = new VideoDecoderPipeline(atlasUrl, options.signal);
        await pipeline.initialize();
        // The matte replays with the atlas, so it restarts on the same boundary.
        if (mattePipeline) {
          mattePipeline.close();
          mattePipeline = await initializeAuxiliaryDecoder(
            matteUrl,
            options.signal,
            'Puzzle Matte'
          );
        }
        // Background Matte follows the same template loop. A sequential decoder
        // cannot seek backwards, so it must restart on the exact same boundary.
        if (backgroundPipeline) {
          backgroundPipeline.close();
          backgroundPipeline = await initializeAuxiliaryDecoder(
            backgroundUrl,
            options.signal,
            'Background matte'
          );
        }
        encoderGate.throwIfUnavailable(options.signal);
        activeAtlasCycle = atlasCycle;
      }
      const trackingTime = getMotionSequencePlaybackTime(motionAssets.tracking, time, motionLoop);
      const trackingFrameIndex = getMotionFrameIndex(motionAssets.tracking, trackingTime);
      const atlasTime = getMotionAtlasFrameTime(motionAssets.tracking, trackingFrameIndex);

      let decoderFrame: VideoFrame | null = null;
      let matteFrame: VideoFrame | null = null;
      let backgroundFrame: VideoFrame | null = null;
      let userFrame: VideoFrame | null = null;
      let userFrame2: VideoFrame | null = null;
      let userFrame3: VideoFrame | null = null;
      let encodedFrame: VideoFrame | null = null;
      try {
        decoderFrame = await pipeline.seekToTime(atlasTime);
        if (!decoderFrame) {
          throw new Error('Не удалось извлечь кадр из VideoDecoder (поток оборван).');
        }

        if (mattePipeline) {
          matteFrame = await mattePipeline.seekToTime(atlasTime).catch(() => null);
        }
        if (backgroundPipeline) {
          backgroundFrame = await backgroundPipeline.seekToTime(atlasTime).catch(() => null);
        }
        // No decoded frame means the pipeline never started or ran dry; seek the
        // plain video element instead, the same fallback the user source uses.
        const useMatteVideoFallback = matteActive && !matteFrame;
        const useBackgroundVideoFallback = backgroundActive && !backgroundFrame;
        if (useMatteVideoFallback || useBackgroundVideoFallback) {
          await seekMotionColorMatteStrict(motionAssets, atlasTime, {
            colorMatte: useMatteVideoFallback,
            backgroundMatte: useBackgroundVideoFallback,
            signal: options.signal
          });
        }

        // Resolve user video frame for this timestamp.
        const primaryTime = getUserSourceFrameTime(userSource, time);
        const secondaryTime = getUserSourceFrameTime(options.userSource2 ?? null, time);
        const tertiaryTime = getUserSourceFrameTime(options.userSource3 ?? null, time);
        let effectiveUserSource: CanvasImageSource | null = userSource;
        let effectiveUserSource2: CanvasImageSource | null = options.userSource2 ?? null;
        let effectiveUserSource3: CanvasImageSource | null = options.userSource3 ?? null;
        if (userPipeline) {
          userFrame = await userPipeline.seekToTime(primaryTime);
          if (userFrame) {
            effectiveUserSource = userFrame;
          }
          // If userFrame is null the pipeline has no frames at all — fall back to HTMLVideoElement.
        } else {
          await seekMotionVideos(null, userSource, primaryTime, options.signal);
        }
        if (userPipeline2) {
          userFrame2 = await userPipeline2.seekToTime(secondaryTime);
          if (userFrame2) {
            effectiveUserSource2 = userFrame2;
          }
        } else {
          await seekMotionVideos(null, options.userSource2 ?? null, secondaryTime, options.signal);
        }
        if (userPipeline3) {
          userFrame3 = await userPipeline3.seekToTime(tertiaryTime);
          if (userFrame3) {
            effectiveUserSource3 = userFrame3;
          }
        } else {
          await seekMotionVideos(null, options.userSource3 ?? null, tertiaryTime, options.signal);
        }

        const trackingQuad = interpolateTrackingQuadAtTime(motionAssets.tracking, trackingTime);
        const trackingQuad2 = motionAssets.tracking2
          ? interpolateTrackingQuadAtTime(motionAssets.tracking2, trackingTime)
          : undefined;
        const trackingQuad3 = motionAssets.tracking3
          ? interpolateTrackingQuadAtTime(motionAssets.tracking3, trackingTime)
          : undefined;
        const squareCanvas = renderFrame(motionAssets, effectiveUserSource, trackingFrameIndex, {
          ...options,
          userSource2: effectiveUserSource2,
          userSource3: effectiveUserSource3,
          renderSize: internalSquareRenderSize,
          screenRenderScale: options.screenRenderScale ?? MOTION_EXPORT_SCREEN_RENDER_SCALE,
          atlasSourceOverride: decoderFrame,
          colorMatteSourceOverride: matteFrame,
          backgroundMatteSourceOverride: backgroundFrame,
          trackingQuadOverride: trackingQuad,
          trackingQuadOverride2: trackingQuad2,
          trackingQuadOverride3: trackingQuad3,
          realtime: false
        });
        const canvas = createMotionViewportCanvas(squareCanvas, resizeMode, {
          squareRenderSize: internalSquareRenderSize,
          targetWidth: exportViewport.outputWidth,
          targetHeight: exportViewport.outputHeight
        });

        const timestamp = Math.round((frameIndex * 1_000_000) / fps);
        const frameDuration = Math.round(1_000_000 / fps);
        encodedFrame = new VideoFrame(canvas, {
          timestamp,
          duration: frameDuration
        });

        encoder.encode(encodedFrame, {
          keyFrame: frameIndex % DEFAULT_MOTION_GOP === 0
        });
      } finally {
        encodedFrame?.close();
        userFrame?.close();
        userFrame2?.close();
        userFrame3?.close();
        matteFrame?.close();
        backgroundFrame?.close();
        decoderFrame?.close();
      }
      await Promise.all([
        encoderGate.waitForCapacity(options.signal),
        muxer.waitForCapacity()
      ]);

      options.onProgress?.(frameIndex + 1, totalFrames);
      if (frameIndex % 2 === 1) {
        await yieldToRenderControls(options.signal);
      }
    }

    options.onStageChange?.('packaging');
    await encoderGate.waitForOperation(encoder.flush(), options.signal, 'Video packaging');
    encoderGate.throwIfUnavailable(options.signal);
    if (audioWriteResult) {
      const audioError = await audioWriteResult;
      if (audioError) {
        throw audioError;
      }
    }
    return await muxer.finalize();
  } finally {
    options.signal?.removeEventListener('abort', handleAbort);
    if (encoder.state !== 'closed') {
      encoder.close();
    }
    pipeline.close();
    mattePipeline?.close();
    backgroundPipeline?.close();
    userPipeline?.close();
    userPipeline2?.close();
    userPipeline3?.close();
    userAudio?.dispose();
    await audioWriteResult;
    await muxer?.cancel();
  }
}

function getUserSourceFrameTime(source: CanvasImageSource | null, time: number): number {
  if (!(source instanceof HTMLVideoElement) || !Number.isFinite(source.duration) || source.duration <= 0) {
    return Math.max(0, time);
  }
  return Math.min(Math.max(0, time), Math.max(0, source.duration - 1 / 120));
}
